import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "cm_ios_install_dismissed";
const VISIT_COUNT_KEY = "cm_visit_count";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

const setCookie = (name, value, days = 90) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
};

const isIOSSafari = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (!isIOS) return false;

  const isChromeIOS = /CriOS/i.test(ua);
  const isFirefoxIOS = /FxiOS/i.test(ua);
  const isEdgeIOS = /EdgiOS/i.test(ua);
  const isOperaIOS = /OPiOS/i.test(ua);
  if (isChromeIOS || isFirefoxIOS || isEdgeIOS || isOperaIOS) return false;

  const isInAppBrowser =
    /FBAN|FBAV|FB_IAB|Instagram|Snapchat|Twitter|X-Client|Pinterest|Line\/|MicroMessenger/i.test(ua);
  if (isInAppBrowser) return false;

  return /Safari/i.test(ua);
};

const isAlreadyInstalled = () => {
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
};

function IOSInstallPrompt() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOSSafari() || isAlreadyInstalled()) return;

    const dismissed =
      localStorage.getItem(DISMISS_KEY) === "true" || getCookie(DISMISS_KEY) === "true";
    if (dismissed) return;

    let visitCount = 0;
    try {
      visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10) || 0;
    } catch {
      visitCount = 0;
    }
    const nextCount = visitCount + 1;
    try {
      localStorage.setItem(VISIT_COUNT_KEY, String(nextCount));
    } catch {}

    if (nextCount < 2) return;

    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "true"); } catch {}
    setCookie(DISMISS_KEY, "true");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10001,
        padding: "0 12px 16px 12px",
        pointerEvents: "none",
      }}
      role="dialog"
      aria-modal="false"
      aria-labelledby="ios-install-title"
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "18px 16px 14px 16px",
          maxWidth: "420px",
          margin: "0 auto",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.18)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
          pointerEvents: "auto",
          position: "relative",
        }}
      >
        <button
          onClick={handleDismiss}
          aria-label={t("iosInstall.dismiss", "Dismiss")}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "transparent",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X size={18} />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <img
            src="/logo3.png"
            alt=""
            width={44}
            height={44}
            style={{ borderRadius: 10, flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <h2
              id="ios-install-title"
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#1f2937",
                lineHeight: 1.3,
              }}
            >
              {t("iosInstall.title", "Install CompareMechanic")}
            </h2>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: 13,
                color: "#6b7280",
                lineHeight: 1.4,
              }}
            >
              {t("iosInstall.subtitle", "Add to your Home Screen for quick, app-like access.")}
            </p>
          </div>
        </div>

        <ol
          style={{
            margin: "0 0 4px 0",
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <li style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "#374151" }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 7,
                background: "#eff6ff",
                color: "#2563eb",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Share size={15} />
            </span>
            <span>
              {t("iosInstall.step1", "Tap the Share button in Safari")}
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "#374151" }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 7,
                background: "#eff6ff",
                color: "#2563eb",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Plus size={15} />
            </span>
            <span>
              {t("iosInstall.step2", "Choose \"Add to Home Screen\"")}
            </span>
          </li>
        </ol>
      </div>
    </div>
  );
}

export default IOSInstallPrompt;
